import '../styles/globals.scss';
import { AdBox, AdSlots } from '../components/ad-box.component';
import Head from 'next/head';
import HeaderComponent from '../components/header.component';
import React, { useEffect } from 'react';
import { AppProps } from 'next/app';
import measuresIndex from '../ssr-data/build/route-content/measures-index.json';
import { LinkRows } from '../components/link-blocks.component';

function getBreadcrumbJsonLd(pageProps: any) {
  if (!pageProps?.from?.type || !pageProps?.fromDescription || !pageProps?.toDescription) return null;
  // Canonical name from the route data (generator: measures.ts) — must match the visible
  // breadcrumb, or the structured data contradicts the page.
  const measure = pageProps.from.readableType;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.xconvert.com' },
      { '@type': 'ListItem', position: 2, name: 'Unit Converter', item: 'https://www.xconvert.com/unit-converter' },
      { '@type': 'ListItem', position: 3, name: measure, item: `https://www.xconvert.com/unit-converter/${pageProps.from.type}` },
      { '@type': 'ListItem', position: 4, name: `${pageProps.fromDescription.plural} to ${pageProps.toDescription.plural}` },
    ],
  };
}

const softwareAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'XConvert Unit Converter',
  applicationCategory: 'UtilityApplication',
  operatingSystem: 'Any',
  url: 'https://www.xconvert.com/unit-converter',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
};

const webSiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'XConvert Unit Converter',
  url: 'https://www.xconvert.com/unit-converter',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.xconvert.com/unit-converter?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

// Every measure hub, from the generator-emitted catalog (unit-converter-ssr measures.ts).
// _app has no getStaticProps, so this is a build-time import: webpack inlines it at export.
// Never hand-maintain this list — the previous hardcoded array had drifted to 14 of 41
// measures, so 27 hubs (including every measure added in 2026-07) had no site-wide link.
const footerMeasures: { measure: string; readableName: string; url: string }[] = measuresIndex;

interface MyAppProps extends AppProps {
}

class MyApp extends React.Component<MyAppProps> {

  componentDidMount() {
  }

  render() {
    const { Component, pageProps } = this.props;
    const breadcrumbJsonLd = getBreadcrumbJsonLd(pageProps);

    return (
      <>
        <Head>
          <title>{pageProps?.title || 'Home Page'}</title>
          <meta name="description" content={pageProps?.description || 'Description'} />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <meta property="og:title" content={pageProps?.title || 'Unit Converter'} />
          <meta property="og:description" content={pageProps?.description || 'Free online unit converter'} />
          <meta property="og:type" content="website" />
          {pageProps?.url && <meta property="og:url" content={`https://www.xconvert.com${pageProps.url}`} />}
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={pageProps?.title || 'Unit Converter'} />
          <meta name="twitter:description" content={pageProps?.description || 'Free online unit converter'} />
          {breadcrumbJsonLd && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
          )}
          {!breadcrumbJsonLd && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareAppJsonLd) }} />
          )}
          {!breadcrumbJsonLd && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteJsonLd) }} />
          )}
        </Head>
        <HeaderComponent />
        <div className="xc-shell">
          <aside className="xc-rail xc-rail-left"><div className="xc-rail-sticky"><AdBox slot={AdSlots.desktopRail} variant="rail" /></div></aside>
          <div className="container">
            <Component {...pageProps} />
          </div>
          <aside className="xc-rail xc-rail-right"><div className="xc-rail-sticky"><AdBox slot={AdSlots.desktopRail} variant="rail" /></div></aside>
        </div>
        {/* Link hierarchy (METHOD §4): File tools row + More converters slot, computed per page at build time
            (pageProps.linkBlocks from getStaticProps — _app has none) from the vendored policy; rotating content. */}
        <LinkRows blocks={pageProps?.linkBlocks} />
        <footer className="site-footer">
          <div className="footer-inner">
            <div className="footer-measures">
            {footerMeasures.map(m => (
              <a key={m.measure} href={m.url} className="footer-measure-link">{m.readableName}</a>
            ))}
            </div>
          <div className="footer-bottom">
            <div className="footer-links">
              <a href="https://www.xconvert.com">XConvert</a>
              <a href="/unit-converter">Unit Converter</a>
              <a href="/time-converter">Time Converter</a>
              <a href="/downloads">Downloads</a>
            </div>
            <div className="footer-copy">© {new Date().getFullYear()} XConvert</div>
          </div>
          </div>
        </footer>
      </>
    );
  }
}

export default MyApp;